import React, { useEffect, useMemo, useRef, useState } from 'react';
import Fade from 'react-reveal/Fade';
import { Link, useLocation } from 'react-router-dom';
import urljoin from 'url-join';
import PageRoute from '../../utils/constants/PageRoute';
import { useEMarketplaceLayoutContext } from './EMarketplaceLayoutContext';
import { useAppSwitcher } from './AppSwitcher';
import GA4Helper from '../../utils/helpers/GA4Helper';
import { GA4TrackConstant } from '../../utils/constants/CommonConstants';
import DefaultCompanyPicture from './../../../assets/images/illustration/original-profile-buyer.svg';
import { usePTVNAuthentication } from '@ptvn-react/authentication';
import { ExtendedUserInformation } from '../../models/User';
import { CommonHelper } from '../../utils/helpers/CommonHelper';

export default function Header(
  props: React.PropsWithChildren<any>,
): React.FunctionComponentElement<any> {
  const { title, navbar, isMobileSize, isTabletSize, navMenu } = useEMarketplaceLayoutContext();
  const { extendedUserInformation } = usePTVNAuthentication<ExtendedUserInformation>();
  const { renderLink } = useAppSwitcher(isTabletSize);

  const navToggleRef = useRef<HTMLDivElement>(null);
  const [titleChange, setTitleChange] = useState(false);
  const location = useLocation();
  const isPIService = location.pathname.includes(PageRoute.PI);

  const toggleNavMenuMobile = () => {
    document.body.classList.toggle('nav-open');
  };

  const toggleNavAppMobile = () => {
    document.body.classList.toggle('nav-app-open');
  };

  let userFullName = '';
  const countryCode =
    extendedUserInformation && extendedUserInformation.currentCompanyBranchDetail?.country
      ? extendedUserInformation.currentCompanyBranchDetail?.country.countryCode || 'TH'
      : 'TH';
  if (extendedUserInformation) {
    const firstName =
      CommonHelper.getLang(
        extendedUserInformation.userInformation,
        'firstName',
        'firstNameEN',
        countryCode,
      ) || '';
    const lastName =
      CommonHelper.getLang(
        extendedUserInformation.userInformation,
        'lastName',
        'lastNameEN',
        countryCode,
      ) || '';
    userFullName = `${firstName} ${lastName}`.trim();
  }

  const renderCompanyName = useMemo(() => {
    if (extendedUserInformation?.currentERFXSubCompanyDetail) {
      return (
        <>
          <small id="pi-header-lbl-profile-company-name" className="d-block font-regular">
            {extendedUserInformation.currentERFXSubCompanyDetail.subCompanyName || ''}
          </small>
        </>
      );
    } else if (extendedUserInformation?.currentCompanyBranchDetail) {
      return (
        <>
          <small id="pi-header-lbl-profile-company-name" className="d-block font-regular">
            {CommonHelper.getLang(
              extendedUserInformation.currentCompanyBranchDetail,
              'companyNameLocal',
              'companyNameEn',
              countryCode,
            ) || ''}
          </small>{' '}
          {extendedUserInformation.currentCompanyBranchDetail.branchList[0] && (
            <small
              id="pi-header-lbl-profile-company-name"
              className="d-block font-regular color-sub"
            >
              {extendedUserInformation.currentCompanyBranchDetail.branchList[0].branchNumber}{' '}
              {CommonHelper.getLang(
                extendedUserInformation.currentCompanyBranchDetail.branchList[0],
                'branchNameLocal',
                'branchNameEn',
                countryCode,
              ) || ''}
            </small>
          )}
        </>
      );
    }
    return <></>;
  }, [extendedUserInformation]);

  useEffect(() => {
    const navToggleEl = navToggleRef.current;
    if (navToggleEl) {
      isMobileSize ? navToggleEl.classList.remove('hide') : navToggleEl.classList.add('hide');
    }
    if (!isMobileSize) {
      document.body.classList.remove('nav-open');
    }
  }, [isMobileSize]);

  useEffect(() => {
    setTimeout(() => {
      setTitleChange(true);
    }, 200);
    return () => {
      setTitleChange(false);
    };
  }, [title]);

  useEffect(() => {
    return () => {
      document.body.classList.remove('nav-open');
    };
  }, [location]);

  return (
    <>
      <header className="header">
        <div className="container">
          {isTabletSize && navMenu && (
            <div className="nav-toggle" ref={navToggleRef} onClick={toggleNavMenuMobile}>
              <i id={'header-icon-hamburger'} className="icon-lg-hamburger"></i>
            </div>
          )}
          <div id="pi-header-ptvn-logo" className="logo">
            {!isPIService ? (
              <Link to={urljoin(PageRoute.PI)} />
            ) : (
              <Link to={urljoin(PageRoute.PI)} />
            )}
            {title && (
              <Fade top duration={titleChange ? 200 : 0} when={titleChange}>
                <span id={title.id}>
                  {title.to ? (
                    <Link
                      to={title.to}
                      onClick={() => {
                        GA4Helper.event(
                          GA4TrackConstant.Category.Navigation,
                          GA4TrackConstant.Action.Click,
                          'Home',
                        );
                      }}
                    >
                      {title.node}
                    </Link>
                  ) : (
                    title.node
                  )}
                </span>
              </Fade>
            )}
          </div>

          {navbar}
        </div>
      </header>

      {isTabletSize && navMenu && (
        <>
          <nav className="nav-menu">
            <div className="nav-menu-top">
              <div id="pi-header-ptvn-logo" className="logo">
                <a href=""></a>
                {title && (
                  <span id={title.id}>
                    {title.to ? <Link to={title.to}>{title.node}</Link> : title.node}
                  </span>
                )}
              </div>
              <div className="ml-auto">
                <button type="button" className="btn-icon" onClick={toggleNavMenuMobile}>
                  <i className="icon-close"></i>
                </button>
              </div>
            </div>

            <div className="nav-menu-scroll">
              <div className="mt-16 p-12-16">
                <div className="media">
                  <div className="float-left mr-8">
                    <div className="img-thumbnail thumb-40">
                      <img src={DefaultCompanyPicture} />
                    </div>
                  </div>
                  <div className="media-body">
                    <div className="font-regular primary">{renderCompanyName}</div>
                    <small className="d-block font-regular">{userFullName}</small>
                  </div>
                </div>
              </div>
              <hr />
              {navMenu}
            </div>
          </nav>

          <nav className="nav-app">
            <div className="nav-app-top">
              <button type="button" className="btn btn-icon btn-back" onClick={toggleNavAppMobile}>
                <i className="icon-lg-arrow-left"></i>
              </button>
              <div className="large-title">Pantavanij App</div>
            </div>
            <div className="nav-app-scroll">
              <ul className="mainmenu">{renderLink()}</ul>
            </div>
          </nav>
        </>
      )}
    </>
  );
}
